import type { CSSProperties } from "react";
import { MEMBER_COLORS, PIN_KINDS, type PinKind } from "@tarkov-callout/shared";
import { t } from "../i18n";
import { pinKindColor, pinKindGlyph } from "../map/pinIcon";
import { used } from "../usage";

/**
 * `ping` is a transient marker rather than a kind of pin the server stores
 * forever, and `eraser` places nothing at all: it takes things off the map.
 */
export type Tool = PinKind | "ping" | "eraser";

/** Everything except `self` and `draw`, which sit apart from the numbered presets. */
const PRESET_KINDS = PIN_KINDS.filter(
  (kind) => kind !== "self" && kind !== "draw",
) as Exclude<PinKind, "self" | "draw">[];

interface Props {
  tool: Tool | null;
  onToolChange: (tool: Tool | null) => void;
  /** The current member's colour, so the "self" tool matches the marker it places. */
  selfColor: string;
  /** What the pen will draw in. Defaults to the member's own colour. */
  penColor: string;
  onPenColorChange: (color: string) => void;
  onClearMine: () => void;
}

export function Toolbar({
  tool,
  onToolChange,
  selfColor,
  penColor,
  onPenColorChange,
  onClearMine,
}: Props) {
  const button = (
    value: Tool,
    glyph: string,
    label: string,
    shortcut: string,
    hint: string,
    color: string,
  ) => (
    <button
      key={value}
      type="button"
      className={`tool tool--${value}${tool === value ? " is-active" : ""}`}
      style={{ "--tool-color": color } as CSSProperties}
      onClick={() => onToolChange(tool === value ? null : value)}
      title={`${hint}（${shortcut}）`}
      aria-pressed={tool === value}
    >
      <span className="tool__glyph" aria-hidden="true">
        {glyph}
      </span>
      <span className="tool__label">{label}</span>
      <span className="tool__key">{shortcut}</span>
    </button>
  );

  return (
    <div className="toolbar">
      {PRESET_KINDS.map((kind, index) =>
        button(
          kind,
          pinKindGlyph(kind),
          t(`kind.${kind}`),
          String(index + 1),
          t(`hint.${kind}`),
          pinKindColor(kind, selfColor),
        ),
      )}
      <span className="toolbar__divider" />
      {button(
        "self",
        pinKindGlyph("self"),
        t("kind.self"),
        "E",
        t("hint.self"),
        pinKindColor("self", selfColor),
      )}
      {/* A ping is stored as an objective, so it wears the objective colour. */}
      {button("ping", "◎", t("kind.ping"), "Q", t("hint.ping"), pinKindColor("objective", selfColor))}
      <span className="toolbar__divider" />
      {/*
        The pen stays armed after a stroke -- drawing is something people do in
        several passes, and re-arming between each one would be its own chore.
      */}
      {button("draw", "✎", t("kind.draw"), "D", t("hint.draw"), pinKindColor("draw", penColor))}
      {/*
        Only while the pen is in hand. A row of colours on screen at all times
        would suggest they apply to pins as well, and they do not: a pin wears
        the colour the room gave you so that everyone can read whose it is.
      */}
      {tool === "draw" && (
        <div className="palette" role="group" aria-label={t("tool.penColor")}>
          {MEMBER_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className={`palette__swatch${color === penColor ? " is-active" : ""}`}
              style={{ background: color }}
              aria-label={color === selfColor ? t("tool.penColorMine") : t("tool.penColor")}
              aria-pressed={color === penColor}
              title={color === selfColor ? t("tool.penColorMine") : t("tool.penColor")}
              onClick={() => {
                used("penColor");
                onPenColorChange(color);
              }}
            />
          ))}
        </div>
      )}
      {/*
        A rubber rather than "click the thing and confirm": what people want to
        undo is usually several things at once, and half of them are lines,
        which are the hardest thing on the map to click exactly.
      */}
      {button("eraser", "⌧", t("tool.eraser"), "X", t("hint.eraser"), "var(--text-dim)")}
      <span className="toolbar__divider" />
      <button
        type="button"
        className="tool tool--clear"
        onClick={onClearMine}
        title={t("tool.clearMineHint")}
      >
        <span className="tool__glyph" aria-hidden="true">
          ⌫
        </span>
        <span className="tool__label">{t("tool.clearMine")}</span>
      </button>
    </div>
  );
}
